/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Button,
  getTranslation,
  Icon,
  ProfilePhoto,
  TranslationProvider,
  utils,
} from '@resy/ui-kitchen';
import { loadResyConfigThunk } from '../../../../../ui-kitchen/src/reducers';
import { IDLE } from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import Menu from '../../components/Menu';
import menuItemsConfig from './menuItems';
import './MenuContainer.scss';
import Variation from '../../../../../ui-kitchen/src/components/Experiments/Variation';
import ABTestWithFallback from '../../../../../ui-kitchen/src/components/Experiments/ABTestWithFallback';

const menuContainerPropTypes = {
  $rootScope: PropTypes.shape({
    nav: PropTypes.shape(),
    $broadcast: PropTypes.func,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  configStatus: PropTypes.string.isRequired,
  loadResyConfigThunk: PropTypes.func.isRequired,
  isConciergeUser: PropTypes.bool.isRequired,
  isGdaEnabled: PropTypes.bool,
  isLoggedIn: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isDeltaPage: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isGda: PropTypes.bool,
  onLogin: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  profilePhotoUrl: PropTypes.string,
  isNonUS: PropTypes.bool.isRequired,
};

const MenuContainer = ({
  $rootScope,
  Analytics,
  configStatus,
  loadResyConfigThunk,
  isConciergeUser,
  isGdaEnabled,
  isLoggedIn,
  isMobile,
  isDeltaPage,
  isOpen,
  isGda,
  onLogin,
  onLogout,
  onOpen,
  profilePhotoUrl,
  isNonUS,
}) => {
  const [menuItems, setMenuItems] = useState([]);

  const generateMenuItems = async () => {
    let items = [];

    if (isMobile) {
      const loggedOutMenuItems = [...menuItemsConfig.loggedOut];
      if (isGdaEnabled) loggedOutMenuItems.splice(1, 0, menuItemsConfig.gda[0]);

      if (!isLoggedIn) {
        items = [...loggedOutMenuItems];
      } else {
        items = [
          ...(isConciergeUser ? menuItemsConfig.concierge : []),
          ...menuItemsConfig.loggedIn,
          { key: 'dividerKey', name: 'divider' },
          ...loggedOutMenuItems,
        ];
      }
    } else {
      items = [
        ...(isConciergeUser ? menuItemsConfig.concierge : []),
        ...menuItemsConfig.loggedIn,
        { key: 'dividerKey', name: 'divider' },
      ];
      items.splice(menuItems.length - 2, 0, menuItemsConfig.gdaModal);
    }

    setMenuItems(items);
  };

  useEffect(() => {
    if (configStatus === IDLE) {
      loadResyConfigThunk();
    }
  }, []);

  useEffect(() => {
    generateMenuItems();
  }, [isLoggedIn, isGdaEnabled]);

  const presentOnboardingLite = () => {
    $rootScope.$broadcast('MODAL', 'onboarding-lite');
  };

  const login = () => {
    onLogin();
  };

  const handleLogoutClick = async () => {
    const context = utils.getPageContext(window.location.pathname) ?? 'homepage';

    onLogout();

    await Analytics.event('click link', {
      context,
      value: 'signout',
    });
  };

  const closeMenu = () => {
    if (isOpen) {
      onOpen();
    }
  };

  const renderMenuAndAuthButtonForDesktop = () => (
    <div className="MenuContainer__menu-and-auth-button">
      <Button
        aria-label={getTranslation({
          translationKey: 'close_menu',
          fallback: 'close menu',
        })}
        variant="close"
        className="MenuContainer__close-container"
        onClick={onOpen}
        data-test-id="menu_container-button-close"
      />
      <Menu
        Analytics={Analytics}
        closeMenu={closeMenu}
        isGda={isGda}
        items={menuItems}
        showGdaModal={presentOnboardingLite}
      />
      <div className="MenuContainer__auth-button-container logged-in">
        <ABTestWithFallback flagKey="resy_web-enable-2025_code_freeze_bug_174" fallbackKey="off" variationKeyList={['on', 'off']}>
          <Variation variationKey="on">
            <TranslationProvider translationKey="signout">
              <Button
                variant="no-style"
                onClick={onLogout}
                data-test-id="menu_container-button-logout"
              >
                Sign out
              </Button>
            </TranslationProvider>
          </Variation>
          <Variation variationKey="off">
            <TranslationProvider translationKey="signout">
              <Button
                variant="no-style"
                onClick={handleLogoutClick}
                data-test-id="menu_container-button-logout"
              >
                Sign out
              </Button>
            </TranslationProvider>
          </Variation>
        </ABTestWithFallback>
      </div>
    </div>
  );

  const renderDesktop = () => (
    <>
      {!isLoggedIn && (
      <TranslationProvider translationKey="login">
        <Button
          variant="login"
          onClick={login}
          data-test-id="menu_container-button-log_in"
        >
          Log in
        </Button>
      </TranslationProvider>
      )}

      {isLoggedIn && (
      <Button
        aria-expanded={isOpen}
        variant="no-style"
        className={cx('ResyIcon__container',
          { 'ResyIcon__container--rga': isGda })}
        onClick={onOpen}
        data-test-id="menu_container-button-profile_photo"
        data-dd-privacy="mask"
      >
        <ProfilePhoto
          isGda={isGda}
          url={profilePhotoUrl}
        />
      </Button>
      )}
      {isLoggedIn && isOpen && renderMenuAndAuthButtonForDesktop()}
    </>
  );

  const renderMenuAndAuthButtonForMobile = () => {
    if (!isLoggedIn) {
      return (
        <div
          className={cx('MenuContainer__menu-and-auth-button', {
            'MenuContainer__menu-and-auth-button--open': isOpen,
          })}
        >
          <Menu Analytics={Analytics} closeMenu={closeMenu} items={menuItems} isNonUS={isNonUS} />
          <div className="MenuContainer__auth-button-container logged-out">
            <TranslationProvider translationKey="login">
              <Button
                variant="login"
                onClick={login}
                data-test-id="menu_container-button-login"
              >
                Log in
              </Button>
            </TranslationProvider>
          </div>
        </div>
      );
    }

    return (
      <div
        className={cx('MenuContainer__menu-and-auth-button', {
          'MenuContainer__menu-and-auth-button--open': isOpen,
        })}
      >
        <Menu Analytics={Analytics} closeMenu={closeMenu} items={menuItems} isNonUS={isNonUS} />
        <div className="MenuContainer__auth-button-container logged-in">
          <ABTestWithFallback flagKey="resy_web-enable-2025_code_freeze_bug_174" fallbackKey="off" variationKeyList={['on', 'off']}>
            <Variation variationKey="on">
              <TranslationProvider translationKey="signout">
                <Button
                  variant="no-style"
                  onClick={onLogout}
                  data-test-id="menu_container-button-logout"
                >
                  Sign out
                </Button>
              </TranslationProvider>
            </Variation>
            <Variation variationKey="off">
              <TranslationProvider translationKey="signout">
                <Button
                  variant="no-style"
                  onClick={handleLogoutClick}
                  data-test-id="menu_container-button-logout"
                >
                  Sign out
                </Button>
              </TranslationProvider>
            </Variation>
          </ABTestWithFallback>
        </div>
      </div>
    );
  };

  const renderMobile = () => (
    <>
      {!isLoggedIn && !isOpen && (
      <Button
        aria-label={getTranslation({
          translationKey: 'menu',
          fallback: 'Menu',
        })}
        variant="no-style"
        className="ResyIcon__container"
        onClick={onOpen}
        data-test-id="menu_container-button-hamburger"
      >
        <Icon name="hamburger" />
      </Button>
      )}
      {isLoggedIn && !isOpen && (
      <Button
        variant="no-style"
        className={cx('ResyIcon__container',
          { 'ResyIcon__container--rga': isGda })}
        onClick={onOpen}
        data-test-id="menu_container-button-profile_photo"
        data-dd-privacy="mask"
      >
        <ProfilePhoto
          isGda={isGda}
          size="36"
          url={profilePhotoUrl}
        />
      </Button>
      )}
      {renderMenuAndAuthButtonForMobile()}
    </>
  );

  const renderMobileDeltaPage = () => (
    <>
      {!isLoggedIn && (
      <TranslationProvider translationKey="login">
        <Button
          variant="login"
          onClick={login}
          data-test-id="menu_container-button-log_in"
        >
          Log in
        </Button>
      </TranslationProvider>
      )}

      {isLoggedIn && !isOpen && (
      <Button
        aria-expanded={isOpen}
        variant="no-style"
        className={cx('ResyIcon__container',
          { 'ResyIcon__container--rga': isGda })}
        onClick={onOpen}
        data-test-id="menu_container-button-profile_photo"
        data-dd-privacy="mask"
      >
        <ProfilePhoto
          isGda={isGda}
          url={profilePhotoUrl}
        />
      </Button>
      )}
      {isLoggedIn && isOpen && renderMenuAndAuthButtonForMobile()}
    </>
  );

  return (
    <div
      className={cx('MenuContainer', {
        'MenuContainer--mobile': isMobile,
        'MenuContainer--desktop': !isMobile,
      })}
    >
      {isMobile && isDeltaPage && renderMobileDeltaPage()}
      {isMobile && !isDeltaPage && renderMobile()}
      {!isMobile && renderDesktop()}
    </div>
  );
};

MenuContainer.propTypes = menuContainerPropTypes;

MenuContainer.defaultProps = {
  isGda: false,
  isGdaEnabled: false,
  profilePhotoUrl: null,
};

export const mapStateToProps = ({ resyConfig, ipdata }) => ({
  configStatus: resyConfig.configStatus,
  isGdaEnabled: resyConfig.config?.enable_global_dining_access,
  isNonUS: ipdata?.isNonUS,
});

const mapDispatchToProps = {
  loadResyConfigThunk,
};

MenuContainer.displayName = 'MenuContainer';

export { menuContainerPropTypes, MenuContainer as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(MenuContainer);
